/**
 * Carries an OAuth authorization request across the existing login flow.
 *
 * The backend's `/oauth/authorize` endpoint, when it finds no logged-in session, parks the validated
 * request server-side and redirects the browser to `/oauth/continue?request_id=...`. Only that opaque
 * id travels through the browser, so nothing the backend already validated (redirect_uri, scope, PKCE
 * challenge) can be tampered with while the user authenticates.
 *
 * The SPA then has one job: remember the id while the user goes through the normal passwordless login,
 * and once they are authenticated, send the browser back to `/oauth/authorize?request_id=...` so the
 * backend can mint the authorization code.
 *
 * `sessionStorage` is used rather than a URL parameter because the login flow performs several
 * client-side navigations (`/login` then `/account-verification`) that would drop it. It is scoped to
 * the tab and cleared as soon as it is consumed.
 */

import {OAUTH_CONTINUE_PATH, OAUTH_REQUEST_ID_PARAM} from '@shared/oauth';

const STORAGE_KEY = 'mavismart.oauth.pendingRequestId';

/** Guards against a hostile or corrupted value being echoed straight back into a URL. */
const REQUEST_ID_PATTERN = /^[A-Za-z0-9]{1,64}$/u;

function safeSessionStorage(): Storage | undefined {
  try {
    return window.sessionStorage;
  } catch {
    // Blocked by browser settings (for instance third-party storage restrictions).
    return undefined;
  }
}

/**
 * Captures the pending request id when the page loads on `/oauth/continue`.
 *
 * Must run before React routes anything: the SPA redirects an unauthenticated visitor to `/login`,
 * which would otherwise discard the query string.
 */
export function capturePendingOauthRequestId(): void {
  if (window.location.pathname !== OAUTH_CONTINUE_PATH) {
    return;
  }
  const requestId = new URLSearchParams(window.location.search).get(OAUTH_REQUEST_ID_PARAM);
  if (requestId === null || !REQUEST_ID_PATTERN.test(requestId)) {
    return;
  }
  safeSessionStorage()?.setItem(STORAGE_KEY, requestId);
}

/** Returns the pending request id, if any, without consuming it. */
export function peekPendingOauthRequestId(): string | undefined {
  const stored = safeSessionStorage()?.getItem(STORAGE_KEY);
  if (stored === null || stored === undefined || !REQUEST_ID_PATTERN.test(stored)) {
    return undefined;
  }
  return stored;
}

/** Clears the pending request id. */
export function clearPendingOauthRequestId(): void {
  safeSessionStorage()?.removeItem(STORAGE_KEY);
}

/**
 * Sends the browser back to the authorization endpoint when a request is pending.
 *
 * Returns true when a navigation was started, so the caller can avoid rendering the app underneath.
 *
 * A full page navigation is required, not a client-side route change: `/oauth/authorize` is served by
 * the backend Lambda, not by the SPA router.
 */
export function resumeOauthAuthorizeIfPending(): boolean {
  const requestId = peekPendingOauthRequestId();
  if (requestId === undefined) {
    return false;
  }
  // Cleared before navigating so a failure at the authorize endpoint cannot put the tab into a
  // redirect loop.
  clearPendingOauthRequestId();
  const target = `/oauth/authorize?${OAUTH_REQUEST_ID_PARAM}=${encodeURIComponent(requestId)}`;
  window.location.assign(target);
  return true;
}
